import React, { useState, useEffect } from 'react';

import moment from 'moment';
import { makeStyles } from '@mui/styles';

import { TransactionsToolbar, TransactionsTable } from './components';

import CircularProgress from '@mui/material/CircularProgress';

import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import { setTransactions, setTransactionPage } from 'store/actions/transaction';
import {
  showErrorSnackbar,
} from "store/actions/snackbarMessage";

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getTransactions, updateTransactions as updateTransactionsAPI } from 'apis/userAPIs';
import { getProcessingFees } from 'apis/reportAPIs';
import { feeDateWindow } from 'util/processingFeeUtils';
import {
  FormControlLabel,
  Switch,
  Grid,
  Alert
} from "@mui/material";

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3),
    flexGrow: 1,
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1.5)
    }
  },
  content: {
    marginTop: theme.spacing(2)
  },
  uiProgess: {
    position: 'fixed',
    zIndex: '1000',
    height: '31px',
    width: '31px',
    left: '50%',
    top: '35%'
  }
}));




const TransactionList = (props) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const history = useHistory();

  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [showDeletedTransactions, setShowDeletedTransactions] = useState(false);

  const selectedTransactionIds = useSelector((state) => state.transaction.ids);
  const account = useSelector((state) => state.account.user);
  const selectedDate = useSelector((state) => state.restaurant.orderFromDate);
  
  const setPage = (page) => dispatch(setTransactionPage(page));
  const setNewTransactions = (trans) => dispatch(setTransactions(trans));
  const showErrorMessage = (msg) => dispatch(showErrorSnackbar(msg));

  const getPage = () => {
    const search = window.location.search;
    const params = new URLSearchParams(search);
    return params.get('page');
  }

  // React Query hook for fetching transactions
  const {
    data: transactionData = { transactions: [], canDeleteTransactions: false, canDeleteInvoiceTransctions: false },
    isLoading,
    isError,
    error,
    refetch
  } = useQuery({
    queryKey: ['transactions', selectedDate, showDeletedTransactions],
    queryFn: () => getTransactions(moment(selectedDate).format('DD-MM-YY'), showDeletedTransactions),
    retry: 2,
    staleTime: 2 * 60 * 1000, // 2 minutes
    onSuccess: (data) => {
      // Update Redux store for backward compatibility
      setNewTransactions(data.transactions);
      if (!getPage()) {
        setPage(0);
      }
    },
    onError: (error) => {
      console.log('Error fetching transactions:', error);
      if (error.response && error.response.status === 403) {
        history.push('/sign-in');
      }
      showErrorMessage('Error in retrieving the data');
    }
  });

  // Processing fees of the day's transactions, fetched once for the whole list. Refunds are recorded
  // when they happen, so the window runs up to a month past the day (never past today); it starts a
  // day early because browser and restaurant timezones may disagree on the day.
  const feeWindow = feeDateWindow(selectedDate || new Date(), { daysBefore: 1 });
  const { data: processingFees } = useQuery({
    queryKey: ['processingFees', feeWindow.startDateStr, feeWindow.endDateStr],
    queryFn: () =>
      getProcessingFees(feeWindow.startDateStr, feeWindow.endDateStr)
        .then((result) => result.fees)
        .catch((feeError) => {
          console.error('getProcessingFees', feeError);
          throw feeError;
        }),
    retry: 1,
    staleTime: 2 * 60 * 1000,
  });

  // Mutation for updating/deleting transactions
  const updateTransactionsMutation = useMutation({
    mutationFn: ({ transactionIds, dateStr, reason }) => 
      updateTransactionsAPI(transactionIds, dateStr, reason),
    onSuccess: () => {
      // Invalidate and refetch transactions after successful update
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
    },
    onError: (error) => {
      console.log('Error updating transactions:', error);
      showErrorMessage('Error updating transactions');
    }
  });

  const selectOrders = (ids) => {
    if (ids.length > 0) {
      setSelectedOrderIds(ids);
    }
  };

  const updateTransactions = async (reason) => {
    if (!selectedTransactionIds || selectedTransactionIds.length === 0) {
      return;
    }

    const dateStr = moment(selectedDate).format("DD-MM-YY");
    updateTransactionsMutation.mutate({ transactionIds: selectedTransactionIds, dateStr, reason });
  };

  const handleSwitchChange = (event) => {
    const name = event.target.name;
    const value = event.target.checked;

    if (name === "showDeletedTransactions") {
      setShowDeletedTransactions(value);
    }
  };



  if (isError) {
    return (
      <main className={classes.content}>
        <Alert severity="error">
          {error?.response?.data?.message || 'Error in retrieving the data'}
        </Alert>
      </main>
    );
  }

  if (isLoading || updateTransactionsMutation.isPending) {
    return (
      <main className={classes.content}>
        <CircularProgress size={150} className={classes.uiProgess} />
      </main>
    );
  }

  return (
    <div className={classes.root}>
      <TransactionsToolbar 
        deleteProxy={updateTransactions} 
        canDeleteTransactions={transactionData.canDeleteTransactions} 
        canDeleteInvoiceTransctions={transactionData.canDeleteInvoiceTransctions} 
      />
      <div className={classes.content}>
        {account && account.role && account.role === "admin" && transactionData.canDeleteTransactions ?
          <Grid className={classes.item} item md={4} sm={6} xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={showDeletedTransactions}
                  onChange={handleSwitchChange}
                  name="showDeletedTransactions"
                  color="primary"
                />
              }
              label="Show deleted transactions"
            />
          </Grid>
          : null}
        <TransactionsTable transactions={transactionData.transactions} processingFees={processingFees} selectorders={selectOrders} />
      </div>
    </div>
  );
};

export default TransactionList;
